const DAY_MS = 24 * 60 * 60 * 1000;
const LOCK_GRACE_MS = DAY_MS;
const SOON_MS = 7 * DAY_MS;

const asDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatShortDate = (value) => {
  const date = asDate(value);
  if (!date) return 'không giới hạn';
  return new Intl.DateTimeFormat('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date);
};

const formatRemaining = (ms) => {
  if (ms <= 0) return '0 giờ';
  if (ms < DAY_MS) {
    return `${Math.max(1, Math.ceil(ms / (60 * 60 * 1000)))} giờ`;
  }
  return `${Math.ceil(ms / DAY_MS)} ngày`;
};

const classroomTitle = (row) => row?.name || row?.code || 'Lớp học';
const accountTitle = (row) => row?.full_name || row?.email || row?.username || 'Học viên';

const push = (items, notification) => {
  items.push({
    id: notification.id,
    tone: notification.tone || 'info',
    title: notification.title,
    message: notification.message,
    timeLabel: notification.timeLabel || 'Bây giờ',
    priority: notification.priority ?? 3,
  });
};

export const getClassroomLockAt = (endsAt) => {
  const ends = asDate(endsAt);
  if (!ends) return null;
  return new Date(ends.getTime() + LOCK_GRACE_MS);
};

export const buildAccountNotifications = ({ accounts = [], classrooms = [], currentUser = null, max = 12 }) => {
  const now = Date.now();
  const items = [];
  const role = (currentUser?.role || '').toLowerCase();
  const scopeLabel = role === 'center' ? 'trung tâm của bạn' : 'hệ thống';

  classrooms.forEach((cls) => {
    const ends = asDate(cls.ends_at);
    if (!ends || cls.purged_at) return;
    const lockAt = getClassroomLockAt(cls.ends_at);
    const studentCount = Number(cls.student_count || 0);

    if (ends.getTime() <= now && lockAt?.getTime() > now) {
      push(items, {
        id: `class-grace-${cls.id}`,
        tone: 'warning',
        priority: 1,
        title: `${classroomTitle(cls)} đã kết thúc`,
        message: `${studentCount} học viên sẽ tự động khóa sau ${formatRemaining(lockAt.getTime() - now)} nếu lớp chưa được gia hạn.`,
        timeLabel: `Khóa lúc ${formatShortDate(lockAt)}`,
      });
      return;
    }

    if (lockAt?.getTime() <= now && studentCount > 0) {
      push(items, {
        id: `class-locked-${cls.id}`,
        tone: 'danger',
        priority: 0,
        title: `${classroomTitle(cls)} đã quá hạn khóa`,
        message: `Lớp đã kết thúc quá 1 ngày. Học viên trong lớp được xem là bị khóa truy cập.`,
        timeLabel: `Kết thúc ${formatShortDate(ends)}`,
      });
      return;
    }

    if (ends.getTime() > now && ends.getTime() - now <= SOON_MS) {
      push(items, {
        id: `class-soon-${cls.id}`,
        tone: 'warning',
        priority: 2,
        title: `${classroomTitle(cls)} sắp hết hạn`,
        message: `Còn ${formatRemaining(ends.getTime() - now)}. Trung tâm nên gia hạn trước khi học viên bị cảnh báo khóa.`,
        timeLabel: formatShortDate(ends),
      });
    }
  });

  accounts
    .filter((account) => (account.role || '').toLowerCase() === 'student')
    .forEach((account) => {
      const ends = asDate(account.classroom_ends_at);
      if (!ends) return;
      const lockAt = getClassroomLockAt(account.classroom_ends_at);
      const status = account.effective_account_status || account.account_status;

      if (lockAt?.getTime() <= now) {
        push(items, {
          id: `student-class-locked-${account.id}`,
          tone: 'danger',
          priority: 0,
          title: `${accountTitle(account)} đã bị khóa theo lớp`,
          message: `Lớp ${account.classroom_code || account.classroom_name || ''} đã kết thúc quá 1 ngày.`,
          timeLabel: status === 'locked' ? 'Đã khóa' : 'Cần đồng bộ',
        });
        return;
      }

      if (ends.getTime() <= now && lockAt?.getTime() > now) {
        push(items, {
          id: `student-class-grace-${account.id}`,
          tone: 'warning',
          priority: 1,
          title: `${accountTitle(account)} còn ${formatRemaining(lockAt.getTime() - now)}`,
          message: `Học viên đang trong thời gian chờ khóa sau khi lớp ${account.classroom_code || account.classroom_name || ''} kết thúc.`,
          timeLabel: `Khóa sau ${formatRemaining(lockAt.getTime() - now)}`,
        });
        return;
      }

      if (ends.getTime() > now && ends.getTime() - now <= 3 * DAY_MS) {
        push(items, {
          id: `student-class-soon-${account.id}`,
          tone: 'info',
          priority: 3,
          title: `${accountTitle(account)} sắp hết khóa học`,
          message: `Lớp ${account.classroom_code || account.classroom_name || ''} còn ${formatRemaining(ends.getTime() - now)}.`,
          timeLabel: formatShortDate(ends),
        });
      }
    });

  const expiringAccounts = accounts.filter((account) => {
    const expires = asDate(account.expires_at);
    if (!expires) return false;
    const status = account.effective_account_status || account.account_status;
    return status === 'active' && expires.getTime() > now && expires.getTime() - now <= SOON_MS;
  });

  if (expiringAccounts.length > 0) {
    push(items, {
      id: 'accounts-expiring-soon',
      tone: 'warning',
      priority: 2,
      title: `${expiringAccounts.length} tài khoản sắp hết hạn`,
      message: `Có tài khoản trong ${scopeLabel} cần được gia hạn trong 7 ngày tới.`,
      timeLabel: 'Cần kiểm tra',
    });
  }

  return items
    .sort((a, b) => a.priority - b.priority)
    .slice(0, max);
};

export const buildStudentNotifications = ({ currentUser = null, assignments = [], remainingQuota = null }) => {
  const now = Date.now();
  const items = [];
  const classroomEnds = asDate(currentUser?.classroom_ends_at);
  const lockAt = getClassroomLockAt(currentUser?.classroom_ends_at);

  if (classroomEnds && lockAt?.getTime() <= now) {
    push(items, {
      id: 'student-class-locked',
      tone: 'danger',
      priority: 0,
      title: 'Tài khoản đã bị khóa theo lớp học',
      message: 'Lớp học đã kết thúc quá 1 ngày. Vui lòng liên hệ trung tâm để được gia hạn.',
      timeLabel: 'Quan trọng',
    });
  } else if (classroomEnds && classroomEnds.getTime() <= now && lockAt?.getTime() > now) {
    push(items, {
      id: 'student-class-grace',
      tone: 'warning',
      priority: 1,
      title: `Lớp học đã kết thúc`,
      message: `Tài khoản sẽ tự động khóa sau ${formatRemaining(lockAt.getTime() - now)} nếu chưa được gia hạn.`,
      timeLabel: `Còn ${formatRemaining(lockAt.getTime() - now)}`,
    });
  } else if (classroomEnds && classroomEnds.getTime() - now <= SOON_MS) {
    push(items, {
      id: 'student-class-soon',
      tone: 'warning',
      priority: 2,
      title: 'Lớp học sắp hết hạn',
      message: `Khóa học của bạn còn ${formatRemaining(classroomEnds.getTime() - now)}.`,
      timeLabel: formatShortDate(classroomEnds),
    });
  }

  const expires = asDate(currentUser?.expires_at);
  if (expires && expires.getTime() > now && expires.getTime() - now <= SOON_MS) {
    push(items, {
      id: 'student-account-soon',
      tone: 'warning',
      priority: 2,
      title: 'Tài khoản sắp hết hạn',
      message: `Tài khoản học viên hết hạn vào ${formatShortDate(expires)}.`,
      timeLabel: formatShortDate(expires),
    });
  }

  if (remainingQuota?.isEmpty) {
    push(items, {
      id: 'student-quota-empty',
      tone: 'danger',
      priority: 1,
      title: 'Đã hết lượt thi thử',
      message: 'Bạn vẫn có thể luyện tập, nhưng cần liên hệ trung tâm để cộng thêm lượt thi thử.',
      timeLabel: `${remainingQuota.used}/${remainingQuota.total} lượt`,
    });
  }

  const available = assignments.filter((row) => row.available).length;
  if (available > 0) {
    push(items, {
      id: 'student-available-assignments',
      tone: 'info',
      priority: 4,
      title: `${available} bài đang mở`,
      message: 'Bạn có thể vào luyện tập hoặc thi thử ngay từ trang tổng quan.',
      timeLabel: 'Sẵn sàng',
    });
  }

  return items.sort((a, b) => a.priority - b.priority).slice(0, 8);
};
